import React, { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { ArrowLeft, Check, Printer, X } from "lucide-react";
// eslint-disable-next-line import/no-unresolved
import "@oppizi-ecosystem/meeting/webcomponent";
// eslint-disable-next-line import/no-unresolved
import "@oppizi-ecosystem/meeting/styles.css";
import CONFIG from "config/config";
import { isProduction } from "utils";
import { Sheet, SheetBody, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "campaign-builder/ui";
import { apiClient } from "module/api";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import { updateCampaign } from "graphQL/mutations/campaign";
import { FORMAT_OPTIONS } from "campaign-builder/domain/constants";
import { parseTimestampStringDate } from "campaign-builder/domain/utils";
import { useCouponCode } from "campaign-builder/hooks/useCouponCode";
import "campaign-builder/types/oppizi-meeting";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import { useCampaignCostPreviewCalculation } from "campaign-builder/hooks/useCampaignCostPreviewCalculation";
import { usePostHog } from "posthog-js/react";
import { useDesignSelection } from "../designSelection";
import {
  ReviewOfferRow,
  useReviewDiscountSummary,
  useDiscountExclusivity,
  DiscountSwapDialog,
  ReviewOfferEntitlement,
  DiscountChoice,
} from "./reviewOffer";
import { TermsAgreementNote } from "campaign-builder/checkout/TermsAgreementNote";
import { getReviewOfferCopy } from "./reviewOffer/reviewOfferCopy";
import { DEFAULT_REVIEW_OFFER_PROGRAM } from "./reviewOffer/types";

interface OrderDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  isPaymentSkipped: boolean;
  onGoToPayment: () => void;
  onBookReviewMeeting: () => void;
  /** Billing-profile gate banner; the wall is at payment, not here. */
  topBanner?: React.ReactNode;
  /**
   * HUB-458: lifts useDiscountExclusivity's discountChoice up to the submit
   * flow (useADMSubmit), which needs it to send `discount_choice` on the
   * checkout request — docs/review-discount-payments-integration.md §2.0.
   * Same lift pattern as `onEntitlementChange` on `ReviewOfferRow` below.
   */
  onDiscountChoiceChange?: (choice: DiscountChoice) => void;
}

interface SavedReturnAddress {
  id: number;
  address: string;
  city: string;
  state: string;
  zipcode: string;
}

// Figma: h36 / px16 / radius8 / border #E2E8F0 / bg #FFF / shadow 0 1px 2px rgba(0,0,0,.05)
const BTN_SECONDARY =
  "box-border flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-solid border-border bg-background px-4 text-sm font-medium text-foreground shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] hover:bg-slate-50 dark:hover:bg-accent";
const BTN_PRIMARY =
  "box-border flex h-9 shrink-0 items-center justify-center rounded-lg bg-[#ef2b55] px-4 text-sm font-medium text-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] hover:bg-[#ef2b55]/90";

const money = (value: number | null | undefined) =>
  `$${(value ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ApplyButton = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label="Apply"
    className="box-border flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#ef2b55] text-white shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] disabled:opacity-50"
  >
    <Check className="h-4 w-4" />
  </button>
);

const InlineInput = ({
  value,
  onChange,
  onClear,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  onClear: () => void;
  placeholder: string;
}) => (
  <div className="box-border flex h-9 flex-1 items-center rounded-lg border border-solid border-border bg-background pl-3 pr-2 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]">
    <input
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="text-foreground placeholder:text-muted-foreground flex-1 border-0 bg-transparent text-sm outline-none"
    />
    <button
      type="button"
      onClick={onClear}
      aria-label="Clear"
      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 shrink-0"
    >
      <X className="h-4 w-4" />
    </button>
  </div>
);

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4 border-b border-solid border-border py-3 last:border-b-0">
    <span className="text-muted-foreground text-sm leading-5">{label}</span>
    <span className="text-foreground text-right text-sm font-medium leading-5">{value}</span>
  </div>
);

export const OrderDetailsDrawer = ({
  open,
  onClose,
  isPaymentSkipped,
  onGoToPayment,
  onBookReviewMeeting,
  topBanner,
  onDiscountChoiceChange,
}: OrderDetailsDrawerProps) => {
  const { campaignData, activeAudienceDetails, refetchCampaign, user } = useCampaignData();
  const { country, client } = useClientConfig();
  const { campaignCostPreview } = useCampaignCostPreviewCalculation({ format: campaignData?.flyerType });
  const { selectedDesign } = useDesignSelection();

  const [returnAddress, setReturnAddress] = useState<SavedReturnAddress | null>(null);
  const [view, setView] = useState<"order" | "meeting">("order");

  useEffect(() => {
    if (open) setView("order");
  }, [open]);

  const onUpdateCampaignCostWithCoupon = async (couponCode: string | null, amountOff: number, percentOff: number) => {
    await updateCampaignExtraData({
      campaignId: campaignData?.id,
      stripeCouponCode: couponCode,
      amountOff,
      percentOff,
    });
    await refetchCampaign();
  };

  const coupon = useCouponCode({
    campaign: campaignData as any,
    country: country as any,
    campaignCostPreview,
    onUpdateCampaignCostWithCoupon,
    client: client as any,
  });
  const [couponInput, setCouponInput] = useState("");

  // Purchase Order Number (AC65-68). Persists onto the campaign via updateCampaign
  // (channel-agnostic — same column CORE uses; xero.js reads it for the invoice).
  const [poOpen, setPoOpen] = useState(false);
  const [poInput, setPoInput] = useState("");
  const [poError, setPoError] = useState<string | null>(null);
  const [isSavingPo, setIsSavingPo] = useState(false);
  // Synchronous guard so a fast double-click can't fire two saves before re-render.
  const savingPoRef = useRef(false);
  const savedPo = campaignData?.purchaseOrderNumber || "";

  const persistPo = useCallback(
    async (value: string) => {
      const id = campaignData?.id;
      if (!id) throw new Error("Missing campaign id");
      // Empty value clears the PO (null), so deleting it propagates to the invoice.
      await updateCampaign({ campaignId: id, purchaseOrderNumber: value || null });
      await refetchCampaign();
    },
    [campaignData?.id, refetchCampaign]
  );

  useEffect(() => {
    if (!open || !campaignData?.returnAddressId) return;
    let active = true;
    apiClient
      .getAllReturnAddresses()
      .then((response: Response) => (response.ok ? response.json() : { returnAddresses: [] }))
      .then((data: { returnAddresses?: SavedReturnAddress[] }) => {
        if (!active) return;
        const list = Array.isArray(data.returnAddresses) ? data.returnAddresses : [];
        setReturnAddress(list.find((a) => a.id === campaignData.returnAddressId) ?? null);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [open, campaignData?.returnAddressId]);

  const handleApplyPo = async () => {
    if (savingPoRef.current) return;
    const value = poInput.trim();
    // Nothing saved and nothing entered → just close. Otherwise persist, treating
    // an emptied field as an intentional clear (persistPo writes null).
    if (!value && !savedPo) {
      setPoOpen(false);
      setPoInput("");
      return;
    }
    savingPoRef.current = true;
    setIsSavingPo(true);
    try {
      await persistPo(value);
      setPoError(null);
      setPoOpen(false);
      setPoInput("");
    } catch (error) {
      // Surface instead of silently dropping — a swallowed failure here left the
      // PO unsaved on the campaign while the UI looked successful.
      console.error("[OrderDetailsDrawer] Failed to save Purchase Order Number", error);
      setPoError("Couldn't save the PO number. Please try again.");
    } finally {
      savingPoRef.current = false;
      setIsSavingPo(false);
    }
  };

  const handleGoToPayment = async () => {
    if (savingPoRef.current) return;
    // Persist a typed-but-not-yet-applied PO (or an unapplied clear) so the change
    // isn't lost when the user skips the "Apply" affordance and goes to payment.
    const pending = poInput.trim();
    if (poOpen && pending !== savedPo) {
      savingPoRef.current = true;
      setIsSavingPo(true);
      try {
        await persistPo(pending);
        setPoError(null);
        setPoOpen(false);
        setPoInput("");
      } catch (error) {
        console.error("[OrderDetailsDrawer] Failed to save Purchase Order Number before payment", error);
        setPoError("Couldn't save the PO number. Please try again.");
        return;
      } finally {
        savingPoRef.current = false;
        setIsSavingPo(false);
      }
    }
    onGoToPayment();
  };

  const format = FORMAT_OPTIONS.find((option) => option.value === campaignData?.flyerType);
  const total = campaignCostPreview.total ?? campaignCostPreview.subtotal;

  // HUB-453 (epic HUB-440): review-for-discount offer, entirely behind
  // is_review_discount_enabled — see ./reviewOffer/ReviewOfferRow.tsx.
  const [reviewEntitlement, setReviewEntitlement] = useState<ReviewOfferEntitlement | null>(null);
  const hasStripeCoupon = !!campaignData?.stripeCouponCode;
  const hasReviewEntitlement = !!reviewEntitlement && reviewEntitlement.status === "active";

  // HUB-454: "one discount per order" exclusivity + swap dialog (S5/S6).
  // discountChoice is the single source of truth for which discount is
  // currently selected for this order — extends B1's lifted state rather
  // than introducing a parallel store.
  const posthog = usePostHog();
  const {
    discountChoice,
    swapDirection,
    isSwapping,
    swapError,
    isSwapBusy,
    openSwapDialog,
    startPendingReviewSwap,
    confirmSwap,
    cancelSwap,
  } = useDiscountExclusivity({
    hasStripeCoupon,
    hasReviewEntitlement,
    onClearCoupon: () => onUpdateCampaignCostWithCoupon(null, 0, 0),
    onSwapped: () =>
      posthog?.capture("review_discount_swapped", { event_category: "Quote", campaign_id: campaignData?.id }),
  });

  const reviewDiscount = useReviewDiscountSummary({
    entitlement: reviewEntitlement,
    subtotal: campaignCostPreview.subtotal,
    total,
    isReviewDiscountChosen: discountChoice === "review",
  });
  const displayTotal = reviewDiscount.isApplied ? reviewDiscount.displayTotal : total;

  // HUB-458: lift discountChoice up so useADMSubmit can send it as
  // `discount_choice` on the checkout request. Mirrors the existing
  // onEntitlementChange lift below.
  useEffect(() => {
    onDiscountChoiceChange?.(discountChoice);
  }, [discountChoice, onDiscountChoiceChange]);

  if (view === "meeting") {
    return (
      <Sheet open={open} onClose={onClose} aria-label="Book a review meeting" className="max-w-[600px]">
        <div className="flex items-center gap-3 border-b border-solid border-border p-6">
          <button
            type="button"
            onClick={() => setView("order")}
            aria-label="Back to order details"
            className="text-muted-foreground hover:text-foreground"
            data-cypress-id="orderDetailsBackToOrder"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold">Book a review meeting</h2>
        </div>
        <SheetBody>
          <oppizi-meeting
            first-name={user?.firstName ?? ""}
            last-name={user?.lastName ?? ""}
            email={user?.email ?? ""}
            country={country?.code}
            redirect-url={`${CONFIG.PLATFORM_BASE_URL}/dashboard/home`}
            ui-mode="embedded"
            is-prod={isProduction()}
            data-cypress-id="orderDetailsBookMeetingEmbed"
          />
        </SheetBody>
      </Sheet>
    );
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      aria-label="Order details"
      className="max-w-[600px]"
      data-copilot-step="ADM Review"
    >
      <SheetHeader>
        <SheetTitle>Order details</SheetTitle>
        <SheetDescription>Review your order before submitting.</SheetDescription>
      </SheetHeader>

      <SheetBody className="space-y-6">
        {topBanner}
        {/* Quote (AC59-62) */}
        <section className="flex flex-col gap-2" data-cypress-id="orderDetailsQuote">
          <h3 className="text-foreground text-sm font-semibold leading-5">Quote</h3>
          <div className="overflow-hidden rounded-lg border border-solid border-border">
            <div className="flex items-center justify-between border-b border-solid border-border bg-muted px-4 py-4 text-sm">
              <span className="text-foreground font-medium">Item description</span>
              <span className="text-muted-foreground flex gap-12">
                <span>Quantity</span>
                <span>Total</span>
              </span>
            </div>
            {campaignCostPreview.lines.map((line) => (
              <div
                key={line.title}
                className="flex items-center justify-between border-b border-solid border-border px-4 py-4 text-sm"
              >
                <span className="text-foreground">{line.title}</span>
                <span className="flex items-center gap-12">
                  <span className="text-muted-foreground">{line.quantity.toLocaleString("en-US")}</span>
                  <span className="text-foreground">{money(line.price)}</span>
                </span>
              </div>
            ))}
            <div className="text-muted-foreground flex items-center justify-end gap-12 border-b border-solid border-border px-4 py-4 text-sm">
              <span>Subtotal</span>
              <span>{money(campaignCostPreview.subtotal)}</span>
            </div>
            {/* Review discount summary line (S4, HUB-453) — between Subtotal and Total per frontend-spec.md */}
            {reviewDiscount.isApplied && (
              <div
                className="flex h-12 items-center justify-between border-b border-solid border-border px-4 text-sm"
                data-cypress-id="reviewOfferDiscountSummaryLine"
              >
                <span className="flex items-center gap-1.5 font-medium text-[#009244] dark:text-[#4ADE80]">
                  <Check className="h-3.5 w-3.5" />
                  {reviewDiscount.label}
                </span>
                <span className="font-semibold text-[#009244] dark:text-[#4ADE80]">
                  −{money(reviewDiscount.amountOff)}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between gap-3 bg-muted px-4 py-3">
              {/* discount coupon (AC60-62) */}
              <div className="flex flex-1 items-center gap-2">
                {reviewDiscount.isApplied ? (
                  <button
                    type="button"
                    onClick={() => openSwapDialog("reviewToCoupon")}
                    title={
                      getReviewOfferCopy(reviewEntitlement?.program ?? DEFAULT_REVIEW_OFFER_PROGRAM).offer
                        .exclusiveTooltip
                    }
                    className={BTN_SECONDARY}
                    data-cypress-id="orderDetailsCouponDisabledByReviewDiscount"
                  >
                    I have a discount coupon
                  </button>
                ) : campaignData?.stripeCouponCode ? (
                  <>
                    <span className="text-foreground flex items-center gap-1.5 text-sm font-medium">
                      {campaignData.stripeCouponCode}
                      <Check className="h-4 w-4 text-success-foreground" />
                    </span>
                    <button
                      type="button"
                      onClick={() => onUpdateCampaignCostWithCoupon(null, 0, 0)}
                      className={BTN_SECONDARY}
                    >
                      Remove
                    </button>
                  </>
                ) : coupon.isCouponCodeOpen ? (
                  <>
                    <InlineInput
                      value={couponInput}
                      onChange={(v) => {
                        setCouponInput(v);
                        coupon.setCouponErrorMessage("");
                      }}
                      onClear={() => {
                        setCouponInput("");
                        coupon.setIsCouponCodeOpen(false);
                      }}
                      placeholder="Enter coupon code"
                    />
                    <ApplyButton onClick={() => coupon.handleApplyCouponCode({ stripeCouponCode: couponInput })} />
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={coupon.handleOpenCouponCode}
                    className={BTN_SECONDARY}
                    data-cypress-id="orderDetailsCouponOpen"
                  >
                    I have a discount coupon
                  </button>
                )}
              </div>
              <div className="text-foreground flex items-center gap-12 text-sm font-semibold">
                <span>Total (Before Tax)</span>
                <span>{money(displayTotal)}</span>
              </div>
            </div>
            <ReviewOfferRow
              campaignId={campaignData?.id}
              countryCode={country?.code}
              discountChoice={discountChoice}
              onRequestSwapToReview={() => openSwapDialog("couponToReview")}
              onStartPendingReviewSwap={startPendingReviewSwap}
              isSwapBusy={isSwapBusy}
              onEntitlementChange={setReviewEntitlement}
            />
          </div>
          {coupon.couponErrorMessage && <p className="text-xs text-destructive">{coupon.couponErrorMessage}</p>}
        </section>

        {/* HUB-454: "one discount per order" swap confirmation (frontend-spec.md S6) */}
        {/* HUB-466: program/percent come from the entitlement being swapped — never a hardcoded 5%. */}
        <DiscountSwapDialog
          direction={swapDirection}
          program={reviewEntitlement?.program}
          percent={reviewEntitlement?.discount_percent}
          isSubmitting={isSwapping}
          hasError={swapError}
          onCancel={cancelSwap}
          onConfirm={confirmSwap}
        />

        {/* Campaign specifics (AC63) */}
        <section
          className="overflow-hidden rounded-lg border border-solid border-border"
          data-cypress-id="orderDetailsCampaignSpecifics"
        >
          <div className="text-foreground border-b border-solid border-border bg-muted px-4 py-3 text-sm font-semibold">
            Campaign specifics
          </div>
          <div className="px-4">
            <DetailRow label="Campaign name" value={campaignData?.campaignName} />
            <DetailRow
              label="Launch date"
              value={
                campaignData?.startDate
                  ? dayjs(parseTimestampStringDate(campaignData.startDate)).format("MMM DD, YYYY")
                  : "—"
              }
            />
            <DetailRow
              label="Addressee moved"
              value={campaignData?.updateAddressOnMove ? "Send to new address" : "Don't forward"}
            />
            <DetailRow
              label="Return Address"
              value={
                returnAddress
                  ? `${returnAddress.address}, ${returnAddress.city}, ${returnAddress.state}, ${returnAddress.zipcode}`
                  : "—"
              }
            />
            <DetailRow
              label="Audience"
              value={activeAudienceDetails.map((audience) => (
                <div key={audience.id}>
                  {audience.name} - {audience.metadata.rowsAmount?.toLocaleString("en-US")} units
                </div>
              ))}
            />
            <DetailRow label="Format" value={format ? `${format.label} ${format.topText}` : campaignData?.flyerType} />
          </div>
        </section>

        {/* PO number (AC65-68) + Print (AC69) */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            {savedPo && !poOpen ? (
              <button
                type="button"
                onClick={() => {
                  setPoError(null);
                  setPoInput(savedPo);
                  setPoOpen(true);
                }}
                className={`${BTN_SECONDARY} flex-1 justify-start`}
                data-cypress-id="orderDetailsPoSaved"
              >
                PO Number: {savedPo}
              </button>
            ) : poOpen ? (
              <div className="flex flex-1 items-center gap-2">
                <InlineInput
                  value={poInput}
                  onChange={setPoInput}
                  onClear={() => {
                    setPoError(null);
                    setPoInput("");
                    setPoOpen(false);
                  }}
                  placeholder="Enter Purchase Order Number"
                />
                <ApplyButton onClick={handleApplyPo} disabled={isSavingPo} />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setPoError(null);
                  setPoInput("");
                  setPoOpen(true);
                }}
                className={BTN_SECONDARY}
                data-cypress-id="orderDetailsPoAdd"
              >
                Add Purchase Order Number
              </button>
            )}
            <button
              type="button"
              onClick={() => window.print()}
              className={BTN_SECONDARY}
              data-cypress-id="orderDetailsPrint"
            >
              <Printer className="h-4 w-4" />
              Print order details
            </button>
          </div>
          {poError && (
            <p className="text-destructive text-xs leading-4" data-cypress-id="orderDetailsPoError">
              {poError}
            </p>
          )}
        </div>

        {/* Design preview (AC70) */}
        {selectedDesign?.thumbnailUrl && (
          <section className="flex flex-col gap-2" data-cypress-id="orderDetailsDesignPreview">
            <h3 className="text-foreground text-sm font-semibold leading-5">Design Preview</h3>
            <div className="overflow-hidden rounded-lg border border-solid border-border">
              <img src={selectedDesign.thumbnailUrl} alt={selectedDesign.designName} className="w-full" />
            </div>
          </section>
        )}
      </SheetBody>

      <SheetFooter className="flex flex-col gap-3 border-t border-solid border-border">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex flex-col">
            <span className="text-foreground text-base font-semibold leading-6">
              US$ {money(displayTotal).slice(1)}
            </span>
            <span className="text-muted-foreground text-xs leading-4">Total costs before taxes</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                onBookReviewMeeting();
                setView("meeting");
              }}
              className={BTN_SECONDARY}
              data-cypress-id="orderDetailsBookReviewMeeting"
              data-cy="admBookReviewMeetingButton"
            >
              Book a review meeting
            </button>
            {/* Deliberately anchored with "SubmitAndPay" in the id — this is the
                hard-refused control (never clicked by Copilot control mode) per
                the ui-control skill's deny-list, same convention as EDDM's
                submitAndPayButton (WP-H2). */}
            <button
              type="button"
              onClick={handleGoToPayment}
              disabled={isSavingPo}
              className={`${BTN_PRIMARY} disabled:opacity-50`}
              data-cypress-id="orderDetailsGoToPayment"
              data-cy="admSubmitAndPayButton"
            >
              {isPaymentSkipped ? "Submit" : "Go to payment"}
            </button>
          </div>
        </div>
        <TermsAgreementNote
          actionLabel={isPaymentSkipped ? "Submit" : "Go to payment"}
          className="w-full text-right"
          dataCy="admTermsAcceptNote"
        />
      </SheetFooter>
    </Sheet>
  );
};
